// src/components/NascarGfsPage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";

/*
  NASCAR GFS RANK EXPLORER — LOOP DATA VERSION

  Source of truth:
    /data/nascar/NASCAR Loop Data.json

  Important:
  - This page intentionally uses GFSRank for modeling/research views.
  - Raw GFS MPH is NOT averaged across tracks.
  - Multi-track aggregates are based on race-level GFS ranks only.
  - Empty selection means "all" for years, tracks, and races.
*/

const BASE = import.meta?.env?.BASE_URL ?? "/";

const LOOP_DATA_SRC = `${BASE}data/nascar/NASCAR%20Loop%20Data.json`;
const TRACK_META_SRC = `${BASE}data/nascar/track-metadata.json`;

const seriesConfig = (seriesKey) => {
  const k = String(seriesKey || "cup").toLowerCase();
  const safe = k === "xfinity" || k === "oreilly" || k === "trucks" ? k : "cup";
  const internal = safe === "oreilly" ? "xfinity" : safe;

  return {
    seriesKey: internal,
    publicLabel:
      internal === "xfinity" ? "O'Reilly" : internal === "trucks" ? "Trucks" : "Cup",
    loopSeries:
      internal === "xfinity" ? "O'Reilly" : internal === "trucks" ? "Trucks" : "Cup",
    driverData: `${BASE}data/nascar/${internal}/latest/data.json`,
    entryListCandidates: [
      `${BASE}data/nascar/${internal}/latest/site_id.json`,
      `${BASE}data/nascar/${internal}/latest/site-id.json`,
      `${BASE}data/nascar/${internal}/latest/siteid.json`,
      `${BASE}data/nascar/${internal}/latest/projections.json`,
    ],
    numLogoBase: `${BASE}logos/nascar/${internal}`,
  };
};

/* ----------------------------- fetch ----------------------------- */

function useJson(url) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const ctrl = new AbortController();

    setLoading(true);
    setErr(null);

    fetch(url, { cache: "no-store", signal: ctrl.signal })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status} @ ${url}`);
        return r.json();
      })
      .then((j) => {
        if (!alive) return;
        setData(j);
      })
      .catch((e) => {
        if (!alive || e?.name === "AbortError") return;
        setErr(e);
        setData(null);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => {
      alive = false;
      ctrl.abort();
    };
  }, [url]);

  return { data, err, loading };
}

function useOptionalJson(urls) {
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    const ctrl = new AbortController();

    (async () => {
      for (const url of urls || []) {
        if (!url) continue;
        try {
          const res = await fetch(url, {
            cache: "no-store",
            signal: ctrl.signal,
          });
          if (!res.ok) continue;
          const j = await res.json();
          if (alive) {
            setData(j);
            return;
          }
        } catch (e) {
          if (e?.name === "AbortError") return;
        }
      }

      if (alive) setData(null);
    })();

    return () => {
      alive = false;
      ctrl.abort();
    };
  }, [JSON.stringify(urls || [])]);

  return data;
}

function useLastUpdated(url) {
  const [updatedAt, setUpdatedAt] = useState(null);

  useEffect(() => {
    let alive = true;
    const ctrl = new AbortController();

    (async () => {
      try {
        const h = await fetch(url, {
          method: "HEAD",
          cache: "no-store",
          signal: ctrl.signal,
        });
        const lm = h.headers.get("last-modified");
        if (alive && lm) {
          const d = new Date(lm);
          if (!Number.isNaN(d.getTime())) setUpdatedAt(d);
        }
      } catch (_) {}
    })();

    return () => {
      alive = false;
      ctrl.abort();
    };
  }, [url]);

  return updatedAt;
}

/* ----------------------------- helpers ----------------------------- */

const num = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(String(v).replace(/,/g, "").trim());
  return Number.isFinite(n) ? n : null;
};

const norm = (s) =>
  String(s ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\./g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const DRIVER_NAME_ALIASES = new Map([
  ["john h nemechek", "john hunter nemechek"],
  ["john hunter nemechek", "john hunter nemechek"],
]);

const canonicalDriverName = (name) => {
  const n = norm(name);
  return DRIVER_NAME_ALIASES.get(n) || n;
};

const driverNameSignatures = (name) => {
  const canonical = canonicalDriverName(name);
  if (!canonical) return [];

  const tokens = canonical.split(" ").filter(Boolean);
  const suffixes = new Set(["jr", "sr", "ii", "iii", "iv"]);
  const suffix =
    tokens.length && suffixes.has(tokens[tokens.length - 1])
      ? tokens[tokens.length - 1]
      : "";

  const core = suffix ? tokens.slice(0, -1) : tokens;
  const first = core[0] || "";
  const last = core.length > 1 ? core[core.length - 1] : "";

  const out = new Set([canonical]);

  // This makes e.g. "John H. Nemechek" and "John Hunter Nemechek"
  // compatible without requiring every middle-name variation to be listed.
  if (first && last) {
    out.add([first, last, suffix].filter(Boolean).join(" "));
  }

  return [...out];
};

const driverNamesMatch = (a, b) => {
  const aa = new Set(driverNameSignatures(a));
  return driverNameSignatures(b).some((sig) => aa.has(sig));
};

const fmt = (v, d = 2) =>
  v == null || !Number.isFinite(v)
    ? "—"
    : Number(v).toLocaleString(undefined, {
        minimumFractionDigits: d,
        maximumFractionDigits: d,
      });

const fmtPct = (v, d = 1) =>
  v == null || !Number.isFinite(v) ? "—" : `${fmt(v * 100, d)}%`;

const fmtDate = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const fmtUpdated = (d) =>
  d
    ? d.toLocaleString(undefined, {
        month: "numeric",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : "—";

const median = (vals) => {
  const x = vals.filter(Number.isFinite).sort((a, b) => a - b);
  if (!x.length) return null;
  const m = Math.floor(x.length / 2);
  return x.length % 2 ? x[m] : (x[m - 1] + x[m]) / 2;
};

const avg = (vals) => {
  const x = vals.filter(Number.isFinite);
  return x.length ? x.reduce((a, b) => a + b, 0) / x.length : null;
};

const weightedAvg = (vals, weights) => {
  let s = 0;
  let w = 0;
  for (let i = 0; i < vals.length; i++) {
    if (!Number.isFinite(vals[i]) || !Number.isFinite(weights[i]) || weights[i] <= 0)
      continue;
    s += vals[i] * weights[i];
    w += weights[i];
  }
  return w > 0 ? s / w : null;
};

const yearOf = (row) => {
  const y = num(row?.Year);
  if (y != null) return Math.trunc(y);
  const d = new Date(row?.RaceDate);
  return Number.isNaN(d.getTime()) ? null : d.getFullYear();
};

const raceKey = (r) =>
  [
    String(r?.Series ?? ""),
    String(r?.Year ?? yearOf(r) ?? ""),
    String(r?.RaceID ?? ""),
    String(r?.RaceDate ?? ""),
    String(r?.Track ?? ""),
  ].join("|");

const groupKey = (row, mode) => {
  if (mode === "car") return String(row?.Car ?? "").trim() || "Unknown";
  if (mode === "team") return String(row?.Team ?? "").trim() || "Unknown";
  if (mode === "manufacturer")
    return String(row?.Manufacturer ?? "").trim() || "Unknown";

  // Driver grouping must use the canonical name so aliases such as
  // John H. Nemechek and John Hunter Nemechek are treated as one driver.
  return canonicalDriverName(row?.Driver) || "unknown";
};

const groupLabel = (row, mode) => {
  if (mode === "car") return String(row?.Car ?? "").trim() || "Unknown";
  if (mode === "team") return String(row?.Team ?? "").trim() || "Unknown";
  if (mode === "manufacturer")
    return String(row?.Manufacturer ?? "").trim() || "Unknown";
  return String(row?.Driver ?? "").trim() || "Unknown";
};

const compare = (a, b, dir = "asc") => {
  const na = num(a);
  const nb = num(b);

  if (na != null && nb != null) return dir === "asc" ? na - nb : nb - na;
  if (na != null) return -1;
  if (nb != null) return 1;

  const sa = String(a ?? "");
  const sb = String(b ?? "");
  return dir === "asc" ? sa.localeCompare(sb) : sb.localeCompare(sa);
};

const rankHeatColor = (value, maxRank = 40, isDark = true) => {
  const v = num(value);
  if (v == null) return null;

  const maxV = Math.max(2, Number(maxRank) || 40);
  const t = Math.max(0, Math.min(1, (v - 1) / (maxV - 1)));

  // Classic conditional formatting:
  // best = green, middle = yellow, worst = red.
  if (isDark) {
    if (t <= 0.20) return "#86efac"; // green
    if (t <= 0.40) return "#bef264"; // lime
    if (t <= 0.60) return "#fde047"; // yellow
    if (t <= 0.80) return "#fb923c"; // orange
    return "#f87171";                // red
  }

  if (t <= 0.20) return "#15803d";
  if (t <= 0.40) return "#65a30d";
  if (t <= 0.60) return "#ca8a04";
  if (t <= 0.80) return "#ea580c";
  return "#dc2626";
};

const escapeCSV = (v) => {
  const s = String(v ?? "");
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

function trackType(meta) {
  if (!meta) return "Other";

  const groups = new Set(
    Array.isArray(meta.custom_groups)
      ? meta.custom_groups.map((x) => norm(x))
      : []
  );
  const size = norm(meta.size_group);

  if (
    meta.is_superspeedway_drafting ||
    groups.has("superspeedways") ||
    groups.has("superspeedway")
  )
    return "Superspeedway";

  if (
    meta.is_road_course ||
    groups.has("road courses") ||
    groups.has("road course") ||
    size === "road course"
  )
    return "Road Course";

  if (groups.has("short flat") || groups.has("short_flat")) return "Short / Flat";
  if (meta.is_high_tire_wear || groups.has("high tire wear"))
    return "High Tire Wear";
  if (meta.is_concrete || groups.has("concrete")) return "Concrete";
  if (groups.has("short") || size === "short track") return "Short";
  if (groups.has("intermediates") || groups.has("intermediate") || size === "intermediate")
    return "Intermediate";
  if (groups.has("large oval") || size === "large oval") return "Large Oval";

  return "Other";
}

/* ----------------------------- UI atoms ----------------------------- */

function MenuShell({ open, children, isDark, width = "360px" }) {
  if (!open) return null;
  return (
    <div
      className={[
        "absolute z-50 mt-2 rounded-2xl border shadow-2xl overflow-hidden",
        isDark
          ? "bg-slate-950 border-slate-800"
          : "bg-white border-slate-200",
      ].join(" ")}
      style={{ width: `min(92vw, ${width})` }}
    >
      {children}
    </div>
  );
}

function FilterButton({ active, children, onClick, isDark, title }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={[
        "h-8 rounded-md border px-3 text-xs font-extrabold transition whitespace-nowrap",
        active
          ? isDark
            ? "bg-sky-500 text-slate-950 border-sky-300"
            : "bg-sky-600 text-white border-sky-600"
          : isDark
          ? "bg-slate-950/70 border-slate-700 text-slate-200 hover:bg-slate-900"
          : "bg-white border-slate-300 text-slate-700 hover:bg-slate-50",
      ].join(" ")}
    >
      {children}
    </button>
  );
}

function Stat({ label, value, sub, isDark }) {
  return (
    <div
      className={[
        "rounded-2xl border px-3 py-3 min-w-0",
        isDark
          ? "bg-slate-950/60 border-slate-800"
          : "bg-white border-slate-200",
      ].join(" ")}
    >
      <div className={isDark ? "text-[10px] uppercase tracking-wide text-slate-500" : "text-[10px] uppercase tracking-wide text-slate-500"}>
        {label}
      </div>
      <div className="mt-1 text-lg font-extrabold tabular-nums truncate">{value}</div>
      {sub ? (
        <div className={isDark ? "mt-0.5 text-[11px] text-slate-400" : "mt-0.5 text-[11px] text-slate-500"}>
          {sub}
        </div>
      ) : null}
    </div>
  );
}

function CheckRow({ checked, label, sub, onChange, isDark }) {
  return (
    <label
      className={[
        "flex cursor-pointer items-start gap-2 rounded-xl border px-2.5 py-2",
        isDark
          ? "border-slate-800 bg-slate-900/40 hover:bg-slate-900/70"
          : "border-slate-200 bg-white hover:bg-slate-50",
      ].join(" ")}
    >
      <input
        type="checkbox"
        className="mt-0.5"
        checked={checked}
        onChange={onChange}
      />
      <span className="min-w-0">
        <span className="block text-xs font-bold truncate">{label}</span>
        {sub ? (
          <span className={isDark ? "block text-[10px] text-slate-500" : "block text-[10px] text-slate-400"}>
            {sub}
          </span>
        ) : null}
      </span>
    </label>
  );
}

/* ----------------------------- page ----------------------------- */

export default function NascarGfsPage({ title, seriesKey }) {
  const cfg = useMemo(() => seriesConfig(seriesKey), [seriesKey]);

  const { data: loopRaw, err, loading } = useJson(LOOP_DATA_SRC);
  const { data: metaRaw } = useJson(TRACK_META_SRC);
  const { data: driverRaw } = useJson(cfg.driverData);
  const entryListRaw = useOptionalJson(cfg.entryListCandidates);
  const updatedAt = useLastUpdated(LOOP_DATA_SRC);

  const [theme, setTheme] = useState("dark");
  const isDark = theme === "dark";

  const [view, setView] = useState("aggregate");
  const [groupBy, setGroupBy] = useState("driver");
  const [aggregateMethod, setAggregateMethod] = useState("average");
  const [minRaces, setMinRaces] = useState(1);
  const [entryListOnly, setEntryListOnly] = useState(true);
  const [includeExhibitionRaces, setIncludeExhibitionRaces] = useState(false);

  const [selectedYears, setSelectedYears] = useState(() => new Set());
  const [selectedTracks, setSelectedTracks] = useState(() => new Set());
  const [selectedTypes, setSelectedTypes] = useState(() => new Set());
  const [selectedRaceKeys, setSelectedRaceKeys] = useState(() => new Set());
  const [customRaceMode, setCustomRaceMode] = useState(false);

  const [yearMenuOpen, setYearMenuOpen] = useState(false);
  const [trackMenuOpen, setTrackMenuOpen] = useState(false);
  const [raceMenuOpen, setRaceMenuOpen] = useState(false);

  const [q, setQ] = useState("");
  const [sort, setSort] = useState({ key: "aggregate_rank", dir: "asc" });

  const yearRef = useRef(null);
  const trackRef = useRef(null);
  const raceRef = useRef(null);

  useEffect(() => {
    const onDown = (e) => {
      if (yearRef.current && !yearRef.current.contains(e.target)) setYearMenuOpen(false);
      if (trackRef.current && !trackRef.current.contains(e.target)) setTrackMenuOpen(false);
      if (raceRef.current && !raceRef.current.contains(e.target)) setRaceMenuOpen(false);
    };
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, []);

  const rawRows = useMemo(() => {
    if (Array.isArray(loopRaw)) return loopRaw;
    if (Array.isArray(loopRaw?.rows)) return loopRaw.rows;
    if (Array.isArray(loopRaw?.data)) return loopRaw.data;
    return [];
  }, [loopRaw]);

  const isCupExhibitionRace = (row) => {
  if (norm(row?.Series) !== "cup") return false;

  const raceName = norm(row?.RaceName);

  return (
    raceName.includes("clash") ||
    raceName.includes("duel") ||
    raceName.includes("all star")
  );
};

  const seriesRows = useMemo(() => {
    return rawRows.filter((r) => {
      const series = norm(r?.Series);

      if (cfg.seriesKey === "xfinity") {
        return series === "oreilly" || series === "o reilly" || series === "xfinity";
      }

      if (cfg.seriesKey === "trucks") {
        return series === "trucks" || series === "truck";
      }

      // Cup only — optionally include Clash, Duel, and All-Star exhibition races
      if (series !== "cup") return false;
      if (!includeExhibitionRaces && isCupExhibitionRace(r)) return false;

      return true;
    });
  }, [rawRows, cfg.seriesKey, includeExhibitionRaces]);

  /* ---------------- current entry-list drivers ---------------- */

  const entryListDrivers = useMemo(() => {
    const out = new Set();

    const addName = (name) => {
      for (const sig of driverNameSignatures(name)) {
        if (sig) out.add(sig);
      }
    };

    const addRows = (raw) => {
      const arr = Array.isArray(raw)
        ? raw
        : Array.isArray(raw?.rows)
        ? raw.rows
        : Array.isArray(raw?.data)
        ? raw.data
        : Array.isArray(raw?.players)
        ? raw.players
        : Array.isArray(raw?.drivers)
        ? raw.drivers
        : [];

      for (const row of arr) {
        if (typeof row === "string") {
          addName(row);
          continue;
        }

        const keys = Object.keys(row || {});
        const nameKey =
          keys.find((k) => /^driver$/i.test(k)) ||
          keys.find((k) => /^driver\s*name$/i.test(k)) ||
          keys.find((k) => /^driver_name$/i.test(k)) ||
          keys.find((k) => /^name$/i.test(k)) ||
          keys.find((k) => /^player$/i.test(k)) ||
          keys.find((k) => /^player\s*name$/i.test(k));

        if (nameKey) {
          addName(row?.[nameKey]);
        }
      }
    };

    // data.json is already the page's current driver/projection source.
    addRows(driverRaw);

    // If a separate site-id/projections file exists, union it in.
    addRows(entryListRaw);

    return out;
  }, [driverRaw, entryListRaw]);

  const effectiveSeriesRows = useMemo(() => {
    if (!entryListOnly || !entryListDrivers.size) return seriesRows;

    return seriesRows.filter((r) =>
      driverNameSignatures(r?.Driver).some((sig) => entryListDrivers.has(sig))
    );
  }, [seriesRows, entryListOnly, entryListDrivers]);


  const matchedEntryDriverCount = useMemo(() => {
    return new Set(effectiveSeriesRows.map((r) => canonicalDriverName(r?.Driver))).size;
  }, [effectiveSeriesRows]);


  /* ---------------- track metadata lookup ---------------- */

  const metaRows = useMemo(() => {
    if (Array.isArray(metaRaw)) return metaRaw;
    if (Array.isArray(metaRaw?.rows)) return metaRaw.rows;
    if (Array.isArray(metaRaw?.data)) return metaRaw.data;
    return [];
  }, [metaRaw]);

  const metaLookup = useMemo(() => {
    const map = new Map();

    for (const m of metaRows) {
      const names = [
        m?.track,
        ...(Array.isArray(m?.aliases) ? m.aliases : []),
      ].filter(Boolean);

      for (const n of names) map.set(norm(n), m);
    }

    return map;
  }, [metaRows]);

  const typeForTrack = (track) => trackType(metaLookup.get(norm(track)));

  /* ---------------- available filter values ---------------- */

  const years = useMemo(() => {
    return Array.from(
      new Set(effectiveSeriesRows.map(yearOf).filter((x) => Number.isFinite(x)))
    ).sort((a, b) => b - a);
  }, [effectiveSeriesRows]);

  const tracks = useMemo(() => {
    return Array.from(
      new Set(
        effectiveSeriesRows
          .map((r) => String(r?.Track ?? "").trim())
          .filter(Boolean)
      )
    ).sort((a, b) => a.localeCompare(b));
  }, [effectiveSeriesRows]);

  const trackTypes = useMemo(() => {
    return Array.from(new Set(tracks.map(typeForTrack))).sort((a, b) =>
      a.localeCompare(b)
    );
  }, [tracks, metaLookup]);

  /* ---------------- base filters: year + type + track ---------------- */

  const baseFilteredRows = useMemo(() => {
    return effectiveSeriesRows.filter((r) => {
      const y = yearOf(r);
      if (selectedYears.size && !selectedYears.has(y)) return false;

      const t = String(r?.Track ?? "").trim();
      if (selectedTracks.size && !selectedTracks.has(norm(t))) return false;

      const type = typeForTrack(t);
      if (selectedTypes.size && !selectedTypes.has(type)) return false;

      const rank = num(r?.GFSRank);
      if (rank == null || rank <= 0) return false;

      return true;
    });
  }, [
    effectiveSeriesRows,
    selectedYears,
    selectedTracks,
    selectedTypes,
    metaLookup,
  ]);

  const races = useMemo(() => {
    const map = new Map();

    for (const r of baseFilteredRows) {
      const k = raceKey(r);
      if (!map.has(k)) {
        map.set(k, {
          key: k,
          year: yearOf(r),
          date: r?.RaceDate || "",
          track: String(r?.Track ?? ""),
          raceName: String(r?.RaceName ?? ""),
          raceID: String(r?.RaceID ?? ""),
        });
      }
    }

    return Array.from(map.values()).sort((a, b) => {
      const da = new Date(a.date).getTime();
      const db = new Date(b.date).getTime();
      if (Number.isFinite(db - da) && db !== da) return db - da;
      return String(b.raceID).localeCompare(String(a.raceID));
    });
  }, [baseFilteredRows]);

  /* Keep custom race selection valid when parent filters change. */
  useEffect(() => {
    if (!customRaceMode) return;
    const valid = new Set(races.map((r) => r.key));
    setSelectedRaceKeys((prev) => {
      const next = new Set([...prev].filter((k) => valid.has(k)));
      return next;
    });
  }, [races, customRaceMode]);

  const filteredRows = useMemo(() => {
    if (!customRaceMode) return baseFilteredRows;
    if (!selectedRaceKeys.size) return [];
    return baseFilteredRows.filter((r) => selectedRaceKeys.has(raceKey(r)));
  }, [baseFilteredRows, customRaceMode, selectedRaceKeys]);

  const filteredRaceCount = useMemo(
    () => new Set(filteredRows.map(raceKey)).size,
    [filteredRows]
  );

  /* ---------------- current driver identity / logo mapping ---------------- */

  const currentDriverMetaMap = useMemo(() => {
    const arr = Array.isArray(driverRaw)
      ? driverRaw
      : Array.isArray(driverRaw?.rows)
      ? driverRaw.rows
      : Array.isArray(driverRaw?.data)
      ? driverRaw.data
      : [];

    const map = new Map();

    const findKey = (row, tests) => {
      const keys = Object.keys(row || {});
      for (const test of tests) {
        const hit = keys.find((k) => test.test(String(k).trim()));
        if (hit) return hit;
      }
      return "";
    };

    for (const r of arr) {
      const nameKey = findKey(r, [
        /^driver$/i,
        /^driver\s*name$/i,
        /^driver_name$/i,
        /^name$/i,
        /^player$/i,
        /^player\s*name$/i,
      ]);

      if (!nameKey) continue;

      const carKey = findKey(r, [
        /^car$/i,
        /^car\s*#$/i,
        /^car#$/i,
        /^car_number$/i,
        /^number$/i,
        /^#$/i,
      ]);

      const teamKey = findKey(r, [
        /^team$/i,
        /^team\s*name$/i,
        /^team_name$/i,
      ]);

      const manufacturerKey = findKey(r, [
        /^manufacturer$/i,
        /^manu$/i,
        /^make$/i,
      ]);

      const driver = String(r?.[nameKey] ?? "").trim();
      if (!driver) continue;

      const rawCar = carKey ? r?.[carKey] : "";
      const numericCar = num(rawCar);
      const car =
        numericCar != null
          ? String(Math.round(numericCar))
          : String(rawCar ?? "").trim();

      const meta = {
        driver,
        car,
        team: teamKey ? String(r?.[teamKey] ?? "").trim() : "",
        manufacturer: manufacturerKey
          ? String(r?.[manufacturerKey] ?? "").trim()
          : "",
      };

      for (const sig of driverNameSignatures(driver)) {
        if (sig) map.set(sig, meta);
      }
    }

    return map;
  }, [driverRaw]);

  const currentMetaForDriver = (name) => {
    for (const sig of driverNameSignatures(name)) {
      const hit = currentDriverMetaMap.get(sig);
      if (hit) return hit;
    }
    return null;
  };

  const logoForDriver = (name) => {
    const meta = currentMetaForDriver(name);
    const car = num(meta?.car);
    return Number.isFinite(car) ? `${cfg.numLogoBase}/${Math.round(car)}.png` : "";
  };

  /* ---------------- aggregate engine ---------------- */

  const aggregateRows = useMemo(() => {
    const map = new Map();

    for (const r of filteredRows) {
      const rank = num(r?.GFSRank);
      if (rank == null || rank <= 0) continue;

      const currentMeta = currentMetaForDriver(r?.Driver);
      const key = groupKey(r, groupBy);
      const rk = raceKey(r);

      if (!map.has(key)) {
        map.set(key, {
          key,
          label:
            groupBy === "driver"
              ? currentMeta?.driver || groupLabel(r, groupBy)
              : groupLabel(r, groupBy),
          driver: currentMeta?.driver || String(r?.Driver ?? ""),
          car: currentMeta?.car || String(r?.Car ?? ""),
          team: currentMeta?.team || String(r?.Team ?? ""),
          manufacturer:
            currentMeta?.manufacturer || String(r?.Manufacturer ?? ""),
          raceMap: new Map(),
        });
      }

      const rec = map.get(key);

      // If grouping by Driver, prefer the most recent identity metadata.
      const old = rec.raceMap.get(rk);
      const candidate = {
        rank,
        raceKey: rk,
        date: r?.RaceDate || "",
        year: yearOf(r),
        track: r?.Track || "",
        raceName: r?.RaceName || "",
        gflaps: num(r?.GFLaps),
      };

      if (!old || rank < old.rank) rec.raceMap.set(rk, candidate);

      if (groupBy === "driver") {
        const recDate = new Date(rec.latestDate || 0).getTime();
        const thisDate = new Date(r?.RaceDate || 0).getTime();
        if (!rec.latestDate || thisDate >= recDate) {
          rec.latestDate = r?.RaceDate || "";
          rec.car = currentMeta?.car || String(r?.Car ?? "");
          rec.team = currentMeta?.team || String(r?.Team ?? "");
          rec.manufacturer =
            currentMeta?.manufacturer || String(r?.Manufacturer ?? "");
          rec.driver = currentMeta?.driver || String(r?.Driver ?? "");
          rec.label = currentMeta?.driver || String(r?.Driver ?? "");
        }
      }
    }

    const out = [];

    for (const rec of map.values()) {
      const rr = Array.from(rec.raceMap.values());
      const ranks = rr.map((x) => x.rank).filter(Number.isFinite);
      if (ranks.length < minRaces) continue;

      const averageRank = avg(ranks);
      const medianRank = median(ranks);
      const bestRank = ranks.length ? Math.min(...ranks) : null;
      const worstRank = ranks.length ? Math.max(...ranks) : null;

      const newestYear = Math.max(...rr.map((x) => x.year || 0));
      const recentWeights = rr.map((x) => {
        const age = Math.max(0, newestYear - (x.year || newestYear));
        if (age <= 0) return 1.0;
        if (age === 1) return 0.75;
        if (age === 2) return 0.50;
        return 0.30;
      });
      const recentWeightedRank = weightedAvg(ranks, recentWeights);

      const aggregateRank =
        aggregateMethod === "median"
          ? medianRank
          : aggregateMethod === "recent"
          ? recentWeightedRank
          : aggregateMethod === "best"
          ? bestRank
          : averageRank;

      out.push({
        ...rec,
        races: ranks.length,
        aggregate_rank: aggregateRank,
        avg_rank: averageRank,
        median_rank: medianRank,
        recent_rank: recentWeightedRank,
        best_rank: bestRank,
        worst_rank: worstRank,
        top3_pct: ranks.filter((x) => x <= 3).length / ranks.length,
        top5_pct: ranks.filter((x) => x <= 5).length / ranks.length,
      });
    }

    return out;
  }, [filteredRows, groupBy, aggregateMethod, minRaces, currentDriverMetaMap]);

  /* ---------------- race/single-race selection ---------------- */

  const activeRace = useMemo(() => {
    if (!races.length) return null;

    if (customRaceMode && selectedRaceKeys.size === 1) {
      const only = [...selectedRaceKeys][0];
      return races.find((r) => r.key === only) || null;
    }

    return races[0];
  }, [races, customRaceMode, selectedRaceKeys]);

  const singleRaceRows = useMemo(() => {
    if (!activeRace) return [];
    return baseFilteredRows
      .filter((r) => raceKey(r) === activeRace.key)
      .map((r) => {
        const currentMeta = currentMetaForDriver(r?.Driver);
        return {
          ...r,
          Driver: currentMeta?.driver || r?.Driver,
          Car: currentMeta?.car || r?.Car,
          Team: currentMeta?.team || r?.Team,
          Manufacturer: currentMeta?.manufacturer || r?.Manufacturer,
          rank: num(r?.GFSRank),
        };
      })
      .filter((r) => r.rank != null)
      .sort((a, b) => a.rank - b.rank);
  }, [baseFilteredRows, activeRace, currentDriverMetaMap]);

  const raceLogRows = useMemo(() => {
    return filteredRows
      .map((r) => {
        const currentMeta = currentMetaForDriver(r?.Driver);
        return {
          ...r,
          Driver: currentMeta?.driver || r?.Driver,
          Car: currentMeta?.car || r?.Car,
          Team: currentMeta?.team || r?.Team,
          Manufacturer: currentMeta?.manufacturer || r?.Manufacturer,
          rank: num(r?.GFSRank),
        };
      })
      .filter((r) => r.rank != null)
      .sort((a, b) => {
        const da = new Date(a?.RaceDate || 0).getTime();
        const db = new Date(b?.RaceDate || 0).getTime();
        if (db !== da) return db - da;
        return a.rank - b.rank;
      });
  }, [filteredRows, currentDriverMetaMap]);

  /* ---------------- race-by-race matrix ---------------- */

  const raceByRaceData = useMemo(() => {
    const raceOrder = races
      .filter((race) => {
        if (!customRaceMode) return true;
        return selectedRaceKeys.has(race.key);
      })
      .sort((a, b) => {
        const da = new Date(a.date || 0).getTime();
        const db = new Date(b.date || 0).getTime();
        if (da !== db) return da - db;
        return String(a.raceID || "").localeCompare(String(b.raceID || ""));
      });

    const rowMap = new Map();

    for (const r of filteredRows) {
      const driver = String(r?.Driver ?? "").trim();
      if (!driver) continue;

      const currentMeta = currentMetaForDriver(driver);
      const dkey = canonicalDriverName(driver);
      const rk = raceKey(r);
      const rank = num(r?.GFSRank);
      if (rank == null || rank <= 0) continue;

      if (!rowMap.has(dkey)) {
        rowMap.set(dkey, {
          key: dkey,
          driver: currentMeta?.driver || driver,
          car: currentMeta?.car || String(r?.Car ?? ""),
          team: currentMeta?.team || String(r?.Team ?? ""),
          manufacturer:
            currentMeta?.manufacturer || String(r?.Manufacturer ?? ""),
          latestDate: r?.RaceDate || "",
          ranks: {},
        });
      }

      const rec = rowMap.get(dkey);

      const oldDate = new Date(rec.latestDate || 0).getTime();
      const newDate = new Date(r?.RaceDate || 0).getTime();
      if (!rec.latestDate || newDate >= oldDate) {
        rec.driver = currentMeta?.driver || driver;
        rec.car = currentMeta?.car || String(r?.Car ?? "");
        rec.team = currentMeta?.team || String(r?.Team ?? "");
        rec.manufacturer =
          currentMeta?.manufacturer || String(r?.Manufacturer ?? "");
        rec.latestDate = r?.RaceDate || "";
      }

      const existing = rec.ranks[rk];
      if (existing == null || rank < existing) {
        rec.ranks[rk] = rank;
      }
    }

    const rows = Array.from(rowMap.values()).map((rec) => {
      const selectedRanks = raceOrder
        .map((race) => num(rec.ranks?.[race.key]))
        .filter(Number.isFinite);

      return {
        ...rec,
        avg_rank: avg(selectedRanks),
        races_used: selectedRanks.length,
      };
    });

    return {
      races: raceOrder,
      rows,
    };
  }, [
    races,
    filteredRows,
    customRaceMode,
    selectedRaceKeys,
    currentDriverMetaMap,
  ]);

  const raceColumnLabel = (race) => {
    if (!race) return "";
    const selectedYearCount = selectedYears.size;
    const multipleYears = selectedYearCount > 1 || (selectedYearCount === 0 && years.length > 1);
    const baseTrack = race.track || race.raceName || `Race ${race.raceID}`;
    return multipleYears && race.year ? `${race.year} ${baseTrack}` : baseTrack;
  };

  const raceByRaceRows = useMemo(() => {
    const needle = norm(q);

    let rows = raceByRaceData.rows;

    if (needle) {
      rows = rows.filter((r) =>
        norm([r.driver, r.car, r.team, r.manufacturer].join(" ")).includes(needle)
      );
    }

    if (sort.key === "driver") {
      return [...rows].sort((a, b) => compare(a.driver, b.driver, sort.dir));
    }

    if (sort.key === "manufacturer") {
      return [...rows].sort((a, b) => compare(a.manufacturer, b.manufacturer, sort.dir));
    }

    if (sort.key === "team") {
      return [...rows].sort((a, b) => compare(a.team, b.team, sort.dir));
    }

    if (sort.key === "avg_rank") {
      return [...rows].sort((a, b) => compare(a.avg_rank, b.avg_rank, sort.dir));
    }

    if (sort.key?.startsWith("race:")) {
      const rk = sort.key.slice(5);
      return [...rows].sort((a, b) => compare(a.ranks?.[rk], b.ranks?.[rk], sort.dir));
    }

    return [...rows].sort((a, b) => compare(a.driver, b.driver, "asc"));
  }, [raceByRaceData.rows, q, sort]);

  /* ---------------- search/sort ---------------- */

  const displayedRows = useMemo(() => {
    const needle = norm(q);

    let rows =
      view === "aggregate"
        ? aggregateRows
        : view === "racebyrace"
        ? raceByRaceRows
        : view === "single"
        ? singleRaceRows
        : raceLogRows;

    if (view === "racebyrace") {
      return rows;
    }

    if (needle) {
      rows = rows.filter((r) => {
        const hay =
          view === "aggregate"
            ? [
                r.label,
                r.driver,
                r.car,
                r.team,
                r.manufacturer,
              ].join(" ")
            : [
                r.Driver,
                r.Car,
                r.Team,
                r.Manufacturer,
                r.Track,
                r.RaceName,
                r.RaceDate,
              ].join(" ");

        return norm(hay).includes(needle);
      });
    }

    const key = sort.key;
    const dir = sort.dir;

    return [...rows].sort((a, b) => {
      const av = a?.[key];
      const bv = b?.[key];
      return compare(av, bv, dir);
    });
  }, [view, aggregateRows, raceByRaceRows, singleRaceRows, raceLogRows, q, sort]);

  useEffect(() => {
    if (view === "aggregate") {
      setSort({ key: "aggregate_rank", dir: "asc" });
    } else if (view === "racebyrace") {
      setSort({ key: "avg_rank", dir: "asc" });
    } else {
      setSort({ key: "rank", dir: "asc" });
    }
  }, [view, aggregateMethod]);

  const sortBy = (key, defaultDir = "asc") => {
    setSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { key, dir: defaultDir }
    );
  };

  /* ---------------- summary ---------------- */

  const summary = useMemo(() => {
    if (view === "racebyrace") {
      const allRanks = raceByRaceRows.flatMap((r) =>
        Object.values(r.ranks || {}).map(num).filter(Number.isFinite)
      );
      return {
        rows: raceByRaceRows.length,
        races: raceByRaceData.races.length,
        best: allRanks.length ? Math.min(...allRanks) : null,
        avg: avg(allRanks),
      };
    }

    if (view !== "aggregate") {
      const ranks = displayedRows.map((r) => num(r?.rank)).filter(Number.isFinite);
      return {
        rows: displayedRows.length,
        races: view === "single" ? (activeRace ? 1 : 0) : filteredRaceCount,
        best: ranks.length ? Math.min(...ranks) : null,
        avg: avg(ranks),
      };
    }

    const vals = aggregateRows
      .map((r) => num(r?.aggregate_rank))
      .filter(Number.isFinite);

    return {
      rows: aggregateRows.length,
      races: filteredRaceCount,
      best: vals.length ? Math.min(...vals) : null,
      avg: avg(vals),
    };
  }, [
    view,
    displayedRows,
    aggregateRows,
    activeRace,
    filteredRaceCount,
    raceByRaceRows,
    raceByRaceData.races,
  ]);

  /* ---------------- CSV ---------------- */

  const exportCSV = () => {
    let headers = [];
    let data = [];

    if (view === "aggregate") {
      headers = [
        groupBy === "driver"
          ? "Driver"
          : groupBy === "car"
          ? "Car"
          : groupBy === "team"
          ? "Team"
          : "Manufacturer",
        "Races",
        "Aggregate Rank",
        "Average Rank",
        "Median Rank",
        "Recent Weighted Rank",
        "Best Rank",
        "Worst Rank",
        "Top 3 %",
        "Top 5 %",
      ];

      data = displayedRows.map((r) => [
        r.label,
        r.races,
        r.aggregate_rank,
        r.avg_rank,
        r.median_rank,
        r.recent_rank,
        r.best_rank,
        r.worst_rank,
        r.top3_pct,
        r.top5_pct,
      ]);
    } else if (view === "racebyrace") {
      headers = [
        "Driver",
        "Manufacturer",
        "Team",
        "AVG",
        ...raceByRaceData.races.map((race) => raceColumnLabel(race)),
      ];

      data = raceByRaceRows.map((r) => [
        r.driver,
        r.manufacturer,
        r.team,
        r.avg_rank,
        ...raceByRaceData.races.map((race) => r.ranks?.[race.key] ?? ""),
      ]);
    } else {
      headers = [
        "Date",
        "Track",
        "Race",
        "Driver",
        "Car",
        "Team",
        "Manufacturer",
        "GFS Rank",
        "GF Laps",
      ];

      data = displayedRows.map((r) => [
        r.RaceDate,
        r.Track,
        r.RaceName,
        r.Driver,
        r.Car,
        r.Team,
        r.Manufacturer,
        r.rank,
        r.GFLaps,
      ]);
    }

    const csv = [
      headers.map(escapeCSV).join(","),
      ...data.map((row) => row.map(escapeCSV).join(",")),
    ].join("\r\n");

    const blob = new Blob(["\ufeff" + csv], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `nascar_gfs_ranks_${cfg.seriesKey}_${view}_${new Date()
      .toISOString()
      .slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 3000);
  };

  /* ---------------- filter actions ---------------- */

  const toggleYear = (y) => {
    setSelectedYears((prev) => {
      const next = new Set(prev);
      next.has(y) ? next.delete(y) : next.add(y);
      return next;
    });
    setCustomRaceMode(false);
    setSelectedRaceKeys(new Set());
  };

  const toggleTrack = (t) => {
    const k = norm(t);
    setSelectedTracks((prev) => {
      const next = new Set(prev);
      next.has(k) ? next.delete(k) : next.add(k);
      return next;
    });
    setCustomRaceMode(false);
    setSelectedRaceKeys(new Set());
  };

  const toggleType = (t) => {
    setSelectedTypes((prev) => {
      const next = new Set(prev);
      next.has(t) ? next.delete(t) : next.add(t);
      return next;
    });
    setCustomRaceMode(false);
    setSelectedRaceKeys(new Set());
  };

  const startCustomRaceMode = () => {
    setCustomRaceMode(true);
    setSelectedRaceKeys(new Set(races.map((r) => r.key)));
  };

  const toggleRace = (k) => {
    if (!customRaceMode) {
      setCustomRaceMode(true);
      setSelectedRaceKeys(new Set([k]));
      return;
    }

    setSelectedRaceKeys((prev) => {
      const next = new Set(prev);
      next.has(k) ? next.delete(k) : next.add(k);
      return next;
    });
  };

  const resetAll = () => {
    setSelectedYears(new Set());
    setSelectedTracks(new Set());
    setSelectedTypes(new Set());
    setSelectedRaceKeys(new Set());
    setCustomRaceMode(false);
    setView("aggregate");
    setGroupBy("driver");
    setAggregateMethod("average");
    setMinRaces(1);
    setEntryListOnly(true);
    setIncludeExhibitionRaces(false);
    setQ("");
  };

  /* ---------------- display labels ---------------- */

  const yearsLabel =
    selectedYears.size === 0
      ? "All years"
      : selectedYears.size <= 3
      ? [...selectedYears].sort((a, b) => b - a).join(", ")
      : `${selectedYears.size} years`;

  const trackLabel =
    selectedTracks.size === 0 && selectedTypes.size === 0
      ? "All tracks"
      : `${selectedTracks.size ? `${selectedTracks.size} tracks` : ""}${
          selectedTracks.size && selectedTypes.size ? " + " : ""
        }${selectedTypes.size ? `${selectedTypes.size} types` : ""}`;

  const raceLabel = !customRaceMode
    ? `All ${races.length} races`
    : `${selectedRaceKeys.size} of ${races.length} races`;

  const aggregateLabel =
    aggregateMethod === "median"
      ? "Median Rank"
      : aggregateMethod === "recent"
      ? "Recent Weighted Rank"
      : aggregateMethod === "best"
      ? "Best Rank"
      : "Average Rank";

  const titleText =
    title?.replace(/Xfinity/gi, "O'Reilly") ||
    `${cfg.publicLabel} GFS Rank Explorer`;

  /* ---------------- styles ---------------- */

  const outer = isDark
    ? "min-h-screen bg-slate-950 text-slate-100"
    : "min-h-screen bg-slate-100 text-slate-900";

  const shell = isDark
    ? "border-slate-800 bg-slate-900/95"
    : "border-slate-200 bg-white";

  const inputClass = isDark
    ? "bg-slate-950 border-slate-700 text-slate-100 placeholder:text-slate-600"
    : "bg-white border-slate-300 text-slate-900 placeholder:text-slate-400";

  const selectClass = isDark
    ? "bg-slate-950 border-slate-700 text-slate-100"
    : "bg-white border-slate-300 text-slate-900";

  /* ---------------- table cells ---------------- */

  const RankCell = ({ value }) => {
    const v = num(value);
    if (v == null) return <span>—</span>;

    const cls =
      v <= 3
        ? isDark
          ? "text-emerald-300"
          : "text-emerald-700"
        : v <= 5
        ? isDark
          ? "text-lime-300"
          : "text-lime-700"
        : v <= 10
        ? isDark
          ? "text-amber-300"
          : "text-amber-700"
        : isDark
        ? "text-slate-300"
        : "text-slate-700";

    return <span className={`font-extrabold tabular-nums ${cls}`}>{fmt(v, 2)}</span>;
  };

  const Th = ({ k, children, defaultDir = "asc", left = false }) => (
    <th
      onClick={() => sortBy(k, defaultDir)}
      className={[
        "sticky top-0 z-20 cursor-pointer border-b px-3 py-2 text-[11px] font-extrabold whitespace-nowrap",
        left ? "text-left" : "text-center",
        isDark
          ? "bg-slate-900 border-slate-700 text-slate-300"
          : "bg-white border-slate-200 text-slate-600",
      ].join(" ")}
    >
      {children} {sort.key === k ? (sort.dir === "asc" ? "▲" : "▼") : ""}
    </th>
  );

  /* ---------------- render ---------------- */

  return (
    <div className={outer}>
      <div className="w-full px-4 py-5 md:px-6">
        <div className="space-y-4">
          {/* Header */}
          <div
            className={[
              "space-y-4",
              "",
            ].join(" ")}
          >
            <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
              <div className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-1">
                <h1 className="text-xl font-bold tracking-tight">{titleText}</h1>
                <span className={"text-xs font-normal italic " + (isDark ? "text-slate-100" : "text-slate-700")}>Updated: {fmtUpdated(updatedAt)}</span>
              </div>
              <div className="flex flex-wrap items-center gap-2">

                <div
                  className={[
                    "inline-flex rounded-full border p-0.5",
                    isDark
                      ? "border-slate-700 bg-slate-950"
                      : "border-slate-300 bg-white",
                  ].join(" ")}
                >
                  <button
                    className={`rounded-full px-2.5 py-1 text-[11px] ${
                      isDark ? "bg-sky-500 text-slate-950" : "text-slate-500"
                    }`}
                    onClick={() => setTheme("dark")}
                  >
                    Dark
                  </button>
                  <button
                    className={`rounded-full px-2.5 py-1 text-[11px] ${
                      !isDark ? "bg-sky-600 text-white" : "text-slate-500"
                    }`}
                    onClick={() => setTheme("light")}
                  >
                    Light
                  </button>
                </div>
              </div>
            </div>

            {/* View tabs */}
            <div className={`flex flex-wrap items-end justify-between gap-x-6 gap-y-3 rounded-2xl border px-4 py-3 ${shell}`}>
<nav aria-label="NASCAR series" className={"inline-flex flex-wrap items-center gap-5 border-b " + (isDark ? "border-slate-800" : "border-slate-200")}>
                {[
                  ["cup", "Cup"],
                  ["xfinity", "O'Reilly"],
                  ["trucks", "Trucks"],
                ].map(([slug, label]) => (
                  <a
                    key={slug}
                    href={`/nascar/${slug === "xfinity" ? "oreilly" : slug}/gfs`}
                    className={[
                      "inline-flex h-9 items-center border-b-2 pb-2 text-xs font-semibold",
                      slug === cfg.seriesKey
                        ? "border-sky-400 " + (isDark ? "text-white" : "text-slate-900")
                        : isDark
                        ? "border-transparent text-slate-400 hover:text-white"
                        : "border-transparent text-slate-500 hover:text-slate-900",
                    ].join(" ")}
                  >
                    {label}
                  </a>
                ))}

</nav>
            <div className={"inline-flex flex-wrap items-center gap-1 border-b " + (isDark ? "border-slate-800" : "border-slate-200")}>
              {[
                ["aggregate", "Aggregate"],
                ["racebyrace", "Race by Race"],
                ["single", "Single Race"],
                ["log", "Race Log"],
              ].map(([k, label]) => (
                <button type="button"
                  key={k}
                  aria-pressed={view === k}
                  className={"h-9 border-b-2 px-3 text-xs font-semibold transition " + (view === k ? (isDark ? "border-sky-400 text-white" : "border-sky-500 text-slate-900") : (isDark ? "border-transparent text-slate-400 hover:text-white" : "border-transparent text-slate-500 hover:text-slate-900"))}
                  onClick={() => setView(k)}
                >
                  {label}
                </button>
              ))}
            </div>

            </div>
            {/* Filters */}
            <div
              className={[
                "flex flex-wrap items-center gap-2 rounded-2xl border p-3",
                isDark
                  ? "border-slate-800 bg-slate-900/95"
                  : "border-slate-200 bg-slate-50",
              ].join(" ")}
            >
              {cfg.seriesKey === "cup" ? (
                <button
                  type="button"
                  title="Include or exclude Cup exhibition races: Clash, Duel, and All-Star"
                  onClick={() => {
                    setIncludeExhibitionRaces((v) => !v);
                    setCustomRaceMode(false);
                    setSelectedRaceKeys(new Set());
                  }}
                  className={[
                    "inline-flex h-8 items-center gap-2 rounded-md border px-3 text-xs font-extrabold transition",
                    includeExhibitionRaces
                      ? isDark
                        ? "border-amber-400/60 bg-amber-500/10 text-amber-200"
                        : "border-amber-400 bg-amber-50 text-amber-800"
                      : isDark
                      ? "border-slate-700 bg-slate-900/70 text-slate-300 hover:border-slate-600"
                      : "border-slate-300 bg-white text-slate-700 hover:border-slate-400",
                  ].join(" ")}
                >
                  <span
                    className={[
                      "h-2 w-2 rounded-full",
                      includeExhibitionRaces ? "bg-amber-400" : "bg-slate-500",
                    ].join(" ")}
                  />
                  Exhibition: {includeExhibitionRaces ? "Included" : "Excluded"}
                </button>
              ) : null}

              {/* Years */}
              <div ref={yearRef} className="relative">
                <FilterButton
                  isDark={isDark}
                  active={selectedYears.size > 0}
                  onClick={() => setYearMenuOpen((v) => !v)}
                >
                  Years: {yearsLabel} ▾
                </FilterButton>

                <MenuShell open={yearMenuOpen} isDark={isDark} width="300px">
                  <div className="flex items-center justify-between border-b border-slate-800 px-3 py-2">
                    <span className="text-xs font-extrabold">Years</span>
                    <button
                      className="text-[11px] font-bold text-sky-400"
                      onClick={() => {
                        setSelectedYears(new Set());
                        setCustomRaceMode(false);
                        setSelectedRaceKeys(new Set());
                      }}
                    >
                      All years
                    </button>
                  </div>
                  <div className="grid max-h-[300px] grid-cols-2 gap-2 overflow-auto p-3">
                    {years.map((y) => (
                      <CheckRow
                        key={y}
                        checked={selectedYears.has(y)}
                        label={y}
                        onChange={() => toggleYear(y)}
                        isDark={isDark}
                      />
                    ))}
                  </div>
                </MenuShell>
              </div>

              {/* Tracks */}
              <div ref={trackRef} className="relative">
                <FilterButton
                  isDark={isDark}
                  active={selectedTracks.size > 0 || selectedTypes.size > 0}
                  onClick={() => setTrackMenuOpen((v) => !v)}
                >
                  Tracks: {trackLabel} ▾
                </FilterButton>

                <MenuShell open={trackMenuOpen} isDark={isDark} width="500px">
                  <div className="flex items-center justify-between border-b border-slate-800 px-3 py-2">
                    <span className="text-xs font-extrabold">Track selection</span>
                    <button
                      className="text-[11px] font-bold text-sky-400"
                      onClick={() => {
                        setSelectedTracks(new Set());
                        setSelectedTypes(new Set());
                        setCustomRaceMode(false);
                        setSelectedRaceKeys(new Set());
                      }}
                    >
                      Clear
                    </button>
                  </div>

                  <div className="border-b border-slate-800 p-3">
                    <div className="mb-2 text-[10px] font-extrabold uppercase tracking-wide text-slate-500">
                      Track types — multiple allowed
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {trackTypes.map((t) => (
                        <FilterButton
                          key={t}
                          isDark={isDark}
                          active={selectedTypes.has(t)}
                          onClick={() => toggleType(t)}
                        >
                          {t}
                        </FilterButton>
                      ))}
                    </div>
                  </div>

                  <div className="p-3">
                    <div className="mb-2 text-[10px] font-extrabold uppercase tracking-wide text-slate-500">
                      Exact tracks
                    </div>
                    <div className="grid max-h-[320px] grid-cols-1 gap-2 overflow-auto sm:grid-cols-2">
                      {tracks.map((t) => (
                        <CheckRow
                          key={t}
                          checked={selectedTracks.has(norm(t))}
                          label={t}
                          sub={typeForTrack(t)}
                          onChange={() => toggleTrack(t)}
                          isDark={isDark}
                        />
                      ))}
                    </div>
                  </div>
                </MenuShell>
              </div>

              {/* Exact races */}
              <div ref={raceRef} className="relative">
                <FilterButton
                  isDark={isDark}
                  active={customRaceMode}
                  onClick={() => setRaceMenuOpen((v) => !v)}
                >
                  Races: {raceLabel} ▾
                </FilterButton>

                <MenuShell open={raceMenuOpen} isDark={isDark} width="560px">
                  <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-800 px-3 py-2">
                    <div>
                      <div className="text-xs font-extrabold">Exact races</div>
                      <div className="text-[10px] text-slate-500">
                        Built from the current year/track filters
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <button
                        className="text-[11px] font-bold text-sky-400"
                        onClick={() => {
                          setCustomRaceMode(false);
                          setSelectedRaceKeys(new Set());
                        }}
                      >
                        All
                      </button>
                      <button
                        className="text-[11px] font-bold text-sky-400"
                        onClick={startCustomRaceMode}
                      >
                        Select all
                      </button>
                      <button
                        className="text-[11px] font-bold text-sky-400"
                        onClick={() => {
                          setCustomRaceMode(true);
                          setSelectedRaceKeys(new Set());
                        }}
                      >
                        Clear
                      </button>
                    </div>
                  </div>

                  <div className="max-h-[420px] overflow-auto p-3">
                    <div className="space-y-2">
                      {races.map((r) => (
                        <CheckRow
                          key={r.key}
                          checked={
                            customRaceMode
                              ? selectedRaceKeys.has(r.key)
                              : true
                          }
                          label={`${fmtDate(r.date)} — ${r.track}`}
                          sub={r.raceName || `Race ${r.raceID}`}
                          onChange={() => toggleRace(r.key)}
                          isDark={isDark}
                        />
                      ))}
                    </div>
                  </div>
                </MenuShell>
              </div>

              <div
                className={[
                  "inline-flex h-8 items-center gap-2 rounded-md border px-3",
                  entryListOnly
                    ? isDark
                      ? "border-sky-500/50 bg-sky-500/10"
                      : "border-sky-400 bg-sky-50"
                    : isDark
                    ? "border-slate-700 bg-slate-900/70"
                    : "border-slate-300 bg-white",
                ].join(" ")}
              >
                <label
                  className="inline-flex cursor-pointer select-none items-center gap-2 text-xs font-bold"
                  title="Only show drivers found in the current site-id / projections entry-list data"
                >
                  <input
                    type="checkbox"
                    checked={entryListOnly}
                    onChange={(e) => setEntryListOnly(e.target.checked)}
                  />
                  Entry list only
                </label>
                <span className="text-[10px] text-slate-500">
                  {entryListDrivers.size
                    ? `${matchedEntryDriverCount} matched`
                    : "source unavailable"}
                </span>
              </div>

              <button
                type="button"
                onClick={resetAll}
                className={[
                  "ml-auto h-9 rounded-xl border px-3 text-xs font-bold transition",
                  isDark
                    ? "border-slate-800 bg-slate-900/40 text-slate-500 hover:border-slate-700 hover:text-slate-200"
                    : "border-slate-200 bg-white text-slate-500 hover:border-slate-300 hover:text-slate-800",
                ].join(" ")}
              >
                Reset
              </button>
            </div>

            {/* Aggregate-specific controls */}
            {view === "aggregate" ? (
              <div className={`flex flex-wrap items-center gap-2 rounded-2xl border p-3 ${shell}`}>
                <select
                  value={groupBy}
                  onChange={(e) => setGroupBy(e.target.value)}
                  className={`h-8 rounded-md border px-3 text-xs font-bold ${selectClass}`}
                >
                  <option value="driver">Group by Driver</option>
                  <option value="car">Group by Car Number</option>
                  <option value="team">Group by Team</option>
                  <option value="manufacturer">Group by Manufacturer</option>
                </select>

                <select
                  value={aggregateMethod}
                  onChange={(e) => setAggregateMethod(e.target.value)}
                  className={`h-8 rounded-md border px-3 text-xs font-bold ${selectClass}`}
                >
                  <option value="average">Average Rank</option>
                  <option value="median">Median Rank</option>
                  <option value="recent">Recent Weighted Rank</option>
                  <option value="best">Best Rank</option>
                </select>

                <select
                  value={minRaces}
                  onChange={(e) => setMinRaces(Number(e.target.value))}
                  className={`h-8 rounded-md border px-3 text-xs font-bold ${selectClass}`}
                >
                  {[1, 2, 3, 4, 5, 6, 8, 10].map((n) => (
                    <option key={n} value={n}>
                      Min {n} race{n === 1 ? "" : "s"}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}
          </div>

          {/* Compact summary strip */}
          <div className="">
            <div
              className={[
                "grid grid-cols-2 overflow-hidden rounded-2xl border lg:grid-cols-4",
                isDark
                  ? "border-slate-800 bg-slate-900/95"
                  : "border-slate-200 bg-white",
              ].join(" ")}
            >
              {[
                {
                  label: "Races",
                  value: summary.races.toLocaleString(),
                  sub: customRaceMode ? "Custom selection" : "Current filters",
                },
                {
                  label: view === "aggregate" ? "Groups" : view === "racebyrace" ? "Drivers" : "Rows",
                  value: summary.rows.toLocaleString(),
                  sub:
                    view === "aggregate"
                      ? `Grouped by ${groupBy}`
                      : view === "racebyrace"
                      ? "Race-by-race matrix"
                      : "Displayed rows",
                },
                {
                  label: "Avg Displayed Rank",
                  value: fmt(summary.avg, 2),
                  sub: "Lower is better",
                },
                {
                  label: "Rank Source",
                  value:
                    view === "aggregate"
                      ? aggregateLabel
                      : view === "single" || view === "log"
                      ? `GFSRank · Best ${fmt(summary.best, 0)}`
                      : "GFSRank",
                  sub: "Race-level GFS rank",
                },
              ].map((item, idx) => (
                <div
                  key={item.label}
                  className={[
                    "px-4 py-3",
                    idx > 0 ? (isDark ? "border-t border-slate-800 sm:border-l sm:border-t-0" : "border-t border-slate-200 sm:border-l sm:border-t-0") : "",
                    idx === 2 ? (isDark ? "lg:border-l border-slate-800 sm:border-l-0 sm:border-t" : "lg:border-l border-slate-200 sm:border-l-0 sm:border-t") : "",
                    idx === 3 ? (isDark ? "sm:border-l border-slate-800 sm:border-t lg:border-t-0" : "sm:border-l border-slate-200 sm:border-t lg:border-t-0") : "",
                  ].join(" ")}
                >
                  <div className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500">
                    {item.label}
                  </div>
                  <div className="mt-1 truncate text-xl font-extrabold tabular-nums">
                    {item.value}
                  </div>
                  <div className="mt-0.5 text-[11px] text-slate-500">{item.sub}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Search + export */}
          <div className={`flex flex-col gap-3 rounded-2xl border p-3 md:flex-row md:items-center ${shell}`}>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search driver, team, track..."
              className={`h-8 w-full rounded-md border px-3 text-xs md:w-80 ${inputClass}`}
            />

            {view === "single" && activeRace ? (
              <div className="text-xs text-slate-500">
                Showing:{" "}
                <span className={isDark ? "font-bold text-slate-200" : "font-bold text-slate-700"}>
                  {fmtDate(activeRace.date)} — {activeRace.track}
                </span>
              </div>
            ) : null}

            <button
              onClick={exportCSV}
              className="h-8 rounded-md bg-sky-500 px-4 text-xs font-extrabold text-slate-950 md:ml-auto"
            >
              Export CSV
            </button>
          </div>

          {/* Table */}
          <div className={`rounded-2xl border p-3 ${shell}`}>
            {loading ? (
              <div className="py-16 text-center text-sm text-slate-500">
                Loading NASCAR Loop Data…
              </div>
            ) : err ? (
              <div className="rounded-xl border border-red-900/50 bg-red-950/20 p-4 text-sm text-red-300">
                Failed to load NASCAR Loop Data: {String(err?.message || err)}
              </div>
            ) : (
              <div
                className={[
                  "relative isolate max-h-[72vh] overflow-auto rounded-xl border",
                  isDark ? "border-slate-800" : "border-slate-200",
                ].join(" ")}
              >
                <table className="min-w-max w-full border-separate text-[11px]" style={{ borderSpacing: 0 }}>
                  <thead>
                    {view === "aggregate" ? (
                      <tr>
                        <Th k="label" left>
                          {groupBy === "driver"
                            ? "Driver"
                            : groupBy === "car"
                            ? "Car"
                            : groupBy === "team"
                            ? "Team"
                            : "Manufacturer"}
                        </Th>
                        {groupBy === "driver" ? (
                          <>
                            <Th k="car">Car</Th>
                            <Th k="team" left>Team</Th>
                          </>
                        ) : null}
                        <Th k="races" defaultDir="desc">Races</Th>
                        <Th k="aggregate_rank">{aggregateLabel}</Th>
                        <Th k="avg_rank">Avg Rank</Th>
                        <Th k="median_rank">Median</Th>
                        <Th k="best_rank">Best</Th>
                        <Th k="worst_rank">Worst</Th>
                        <Th k="top3_pct" defaultDir="desc">Top 3%</Th>
                        <Th k="top5_pct" defaultDir="desc">Top 5%</Th>
                      </tr>
                    ) : view === "racebyrace" ? (
                      <tr>
                        <th
                          onClick={() => sortBy("driver", "asc")}
                          className={[
                            "sticky top-0 left-0 z-[70] cursor-pointer border-b px-3 py-2 text-left text-[11px] font-extrabold whitespace-nowrap",
                            isDark
                              ? "bg-slate-900 border-slate-700 text-slate-300"
                              : "bg-white border-slate-200 text-slate-600",
                          ].join(" ")}
                          style={{ minWidth: "240px" }}
                        >
                          Driver {sort.key === "driver" ? (sort.dir === "asc" ? "▲" : "▼") : ""}
                        </th>

                        <th
                          onClick={() => sortBy("manufacturer", "asc")}
                          className={[
                            "sticky top-0 z-[70] cursor-pointer border-b px-3 py-2 text-left text-[11px] font-extrabold whitespace-nowrap",
                            isDark
                              ? "bg-slate-900 border-slate-700 text-slate-300"
                              : "bg-white border-slate-200 text-slate-600",
                          ].join(" ")}
                          style={{ left: "240px", minWidth: "110px" }}
                        >
                          Manu {sort.key === "manufacturer" ? (sort.dir === "asc" ? "▲" : "▼") : ""}
                        </th>

                        <th
                          onClick={() => sortBy("team", "asc")}
                          className={[
                            "sticky top-0 z-[70] cursor-pointer border-b px-3 py-2 text-left text-[11px] font-extrabold whitespace-nowrap shadow-[8px_0_10px_-10px_rgba(0,0,0,0.85)]",
                            isDark
                              ? "bg-slate-900 border-slate-700 text-slate-300"
                              : "bg-white border-slate-200 text-slate-600",
                          ].join(" ")}
                          style={{ left: "350px", minWidth: "190px" }}
                        >
                          Team {sort.key === "team" ? (sort.dir === "asc" ? "▲" : "▼") : ""}
                        </th>

                        <Th k="avg_rank">AVG</Th>
                        {raceByRaceData.races.map((race) => (
                          <Th key={race.key} k={`race:${race.key}`}>
                            {raceColumnLabel(race)}
                          </Th>
                        ))}
                      </tr>
                    ) : (
                      <tr>
                        <Th k="rank">GFS Rank</Th>
                        <Th k="Driver" left>Driver</Th>
                        <Th k="Car">Car</Th>
                        <Th k="Team" left>Team</Th>
                        <Th k="Manufacturer">Manufacturer</Th>
                        {view === "log" ? (
                          <>
                            <Th k="RaceDate" defaultDir="desc">Date</Th>
                            <Th k="Track" left>Track</Th>
                            <Th k="RaceName" left>Race</Th>
                          </>
                        ) : null}
                        <Th k="GFLaps" defaultDir="desc">GF Laps</Th>
                      </tr>
                    )}
                  </thead>

                  <tbody>
                    {displayedRows.map((r, i) => {
                      const zebra =
                        i % 2
                          ? isDark
                            ? "bg-slate-900/45"
                            : "bg-slate-50"
                          : isDark
                          ? "bg-slate-950"
                          : "bg-white";

                      const stickyZebra =
                        i % 2
                          ? isDark
                            ? "bg-slate-900"
                            : "bg-slate-50"
                          : isDark
                          ? "bg-slate-950"
                          : "bg-white";

                      if (view === "aggregate") {
                        const icon =
                          groupBy === "driver" ? logoForDriver(r.driver) : "";

                        return (
                          <tr key={r.key} className={zebra}>
                            <td className="sticky left-0 z-10 border-b border-slate-800/60 px-3 py-2 font-extrabold whitespace-nowrap bg-inherit">
                              <div className="flex items-center gap-2">
                                {icon ? (
                                  <img
                                    src={icon}
                                    className="h-5 w-5 object-contain"
                                    onError={(e) => {
                                      e.currentTarget.style.display = "none";
                                    }}
                                  />
                                ) : null}
                                <span>{r.label}</span>
                              </div>
                            </td>

                            {groupBy === "driver" ? (
                              <>
                                <td className="border-b border-slate-800/60 px-3 py-2 text-center">
                                  {r.car || "—"}
                                </td>
                                <td className="border-b border-slate-800/60 px-3 py-2">
                                  {r.team || "—"}
                                </td>
                              </>
                            ) : null}

                            <td className="border-b border-slate-800/60 px-3 py-2 text-center tabular-nums">
                              {r.races}
                            </td>
                            <td className="border-b border-slate-800/60 px-3 py-2 text-center">
                              <RankCell value={r.aggregate_rank} />
                            </td>
                            <td className="border-b border-slate-800/60 px-3 py-2 text-center">
                              <RankCell value={r.avg_rank} />
                            </td>
                            <td className="border-b border-slate-800/60 px-3 py-2 text-center">
                              <RankCell value={r.median_rank} />
                            </td>
                            <td className="border-b border-slate-800/60 px-3 py-2 text-center">
                              <RankCell value={r.best_rank} />
                            </td>
                            <td className="border-b border-slate-800/60 px-3 py-2 text-center">
                              <RankCell value={r.worst_rank} />
                            </td>
                            <td className="border-b border-slate-800/60 px-3 py-2 text-center font-bold tabular-nums">
                              {fmtPct(r.top3_pct)}
                            </td>
                            <td className="border-b border-slate-800/60 px-3 py-2 text-center font-bold tabular-nums">
                              {fmtPct(r.top5_pct)}
                            </td>
                          </tr>
                        );
                      }

                      if (view === "racebyrace") {
                        const icon = logoForDriver(r.driver);
                        const matrixMaxRank = Math.max(
                          2,
                          ...raceByRaceRows.flatMap((row) =>
                            Object.values(row.ranks || {})
                              .map(num)
                              .filter(Number.isFinite)
                          )
                        );

                        return (
                          <tr key={r.key} className={zebra}>
                            <td
                              className={[
                                "sticky left-0 z-40 border-b px-3 py-2 font-extrabold whitespace-nowrap", stickyZebra,
                                isDark ? "border-slate-800/60" : "border-slate-200",
                              ].join(" ")}
                              style={{ minWidth: "240px" }}
                            >
                              <div className="flex items-center gap-2">
                                {icon ? (
                                  <img
                                    src={icon}
                                    className="h-5 w-5 object-contain shrink-0"
                                    onError={(e) => {
                                      e.currentTarget.style.display = "none";
                                    }}
                                  />
                                ) : null}
                                <span>{r.driver || "—"}</span>
                              </div>
                            </td>

                            <td
                              className={[
                                "sticky z-40 border-b px-3 py-2 whitespace-nowrap", stickyZebra,
                                isDark ? "border-slate-800/60" : "border-slate-200",
                              ].join(" ")}
                              style={{ left: "240px", minWidth: "110px" }}
                            >
                              {r.manufacturer || "—"}
                            </td>

                            <td
                              className={[
                                "sticky z-40 border-b px-3 py-2 whitespace-nowrap shadow-[8px_0_10px_-10px_rgba(0,0,0,0.85)]", stickyZebra,
                                isDark ? "border-slate-800/60" : "border-slate-200",
                              ].join(" ")}
                              style={{ left: "350px", minWidth: "190px" }}
                            >
                              {r.team || "—"}
                            </td>

                            <td
                              className={[
                                "border-b px-4 py-2 text-center tabular-nums whitespace-nowrap",
                                isDark ? "border-slate-800/60" : "border-slate-200",
                              ].join(" ")}
                              title={`${r.driver} — average GFS rank across ${r.races_used} selected races`}
                            >
                              <span
                                className="font-extrabold tabular-nums"
                                style={{
                                  color:
                                    rankHeatColor(r.avg_rank, matrixMaxRank, isDark) ||
                                    undefined,
                                }}
                              >
                                {r.avg_rank == null ? "—" : fmt(r.avg_rank, 2)}
                              </span>
                            </td>

                            {raceByRaceData.races.map((race) => {
                              const value = r.ranks?.[race.key];

                              return (
                                <td
                                  key={race.key}
                                  className={[
                                    "border-b px-4 py-2 text-center tabular-nums whitespace-nowrap",
                                    isDark ? "border-slate-800/60" : "border-slate-200",
                                  ].join(" ")}
                                  title={`${r.driver} — ${raceColumnLabel(race)}: ${
                                    value ?? "No rank"
                                  }`}
                                >
                                  {value == null ? (
                                    <span className="text-slate-600">—</span>
                                  ) : (
                                    <span
                                      className="font-extrabold tabular-nums"
                                      style={{
                                        color:
                                          rankHeatColor(
                                            value,
                                            matrixMaxRank,
                                            isDark
                                          ) || undefined,
                                      }}
                                    >
                                      {Math.round(value)}
                                    </span>
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      }

                      const icon = logoForDriver(r.Driver);

                      return (
                        <tr key={`${raceKey(r)}|${norm(r.Driver)}|${i}`} className={zebra}>
                          <td className="border-b border-slate-800/60 px-3 py-2 text-center">
                            <RankCell value={r.rank} />
                          </td>
                          <td className="sticky left-0 z-10 border-b border-slate-800/60 px-3 py-2 font-extrabold whitespace-nowrap bg-inherit">
                            <div className="flex items-center gap-2">
                              {icon ? (
                                <img
                                  src={icon}
                                  className="h-5 w-5 object-contain"
                                  onError={(e) => {
                                    e.currentTarget.style.display = "none";
                                  }}
                                />
                              ) : null}
                              <span>{r.Driver || "—"}</span>
                            </div>
                          </td>
                          <td className="border-b border-slate-800/60 px-3 py-2 text-center">
                            {r.Car || "—"}
                          </td>
                          <td className="border-b border-slate-800/60 px-3 py-2">
                            {r.Team || "—"}
                          </td>
                          <td className="border-b border-slate-800/60 px-3 py-2 text-center">
                            {r.Manufacturer || "—"}
                          </td>

                          {view === "log" ? (
                            <>
                              <td className="border-b border-slate-800/60 px-3 py-2 text-center whitespace-nowrap">
                                {fmtDate(r.RaceDate)}
                              </td>
                              <td className="border-b border-slate-800/60 px-3 py-2 whitespace-nowrap">
                                {r.Track || "—"}
                              </td>
                              <td className="border-b border-slate-800/60 px-3 py-2 whitespace-nowrap">
                                {r.RaceName || "—"}
                              </td>
                            </>
                          ) : null}

                          <td className="border-b border-slate-800/60 px-3 py-2 text-center tabular-nums">
                            {num(r.GFLaps) ?? "—"}
                          </td>
                        </tr>
                      );
                    })}

                    {!displayedRows.length ? (
                      <tr>
                        <td
                          colSpan={20}
                          className="px-4 py-12 text-center text-sm text-slate-500"
                        >
                          No rows match the current selection.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            )}

            <div className="mt-3 text-[11px] leading-5 text-slate-500">
              Aggregate views use <strong>GFSRank only</strong>. Raw GFS MPH is not
              averaged across different tracks. <strong>Race by Race</strong> shows
              an AVG column first, then the selected races from oldest to newest
              left-to-right. Individual race ranks are displayed as whole numbers.
              Average and median
              ranks treat each selected race equally. Recent Weighted Rank uses year
              weights of 1.00 / 0.75 / 0.50 / 0.30 for current / previous /
              two-years-back / older races. The <strong>entry list drivers</strong>
              filter is enabled by default and uses the current data.json plus any
              available site-id/projections source.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
